import { useState, useMemo } from 'react';
import { jsPDF } from 'jspdf';

function SortieRunner({ procedure, run, roles, onToggleStep, onComplete, onAbandon, onBack, onAskJBB }) {
  const [focusMode, setFocusMode] = useState(() => localStorage.getItem('dh_sortieFocus') === 'true');

  const role = roles.find(r => r.id === procedure.roleTag);
  const steps = procedure.steps;
  const progress = run.stepProgress || {};

  const doneCount = useMemo(() => steps.filter((_, i) => progress[`step_${i}`]?.done).length, [steps, progress]);
  const totalCount = steps.length;
  const allDone = doneCount === totalCount && totalCount > 0;

  // Find first unchecked step index
  const currentIndex = useMemo(() => {
    for (let i = 0; i < steps.length; i++) {
      if (!progress[`step_${i}`]?.done) return i;
    }
    return -1;
  }, [steps, progress]);

  function handleToggle(index) {
    onToggleStep(run.id, index);
  }

  function toggleFocusMode() {
    const next = !focusMode;
    setFocusMode(next);
    localStorage.setItem('dh_sortieFocus', String(next));
  }

  function handleComplete() {
    if (!window.confirm('Mark this sortie as complete?')) return;
    onComplete(run.id);
    onBack();
  }

  function handleAbandon() {
    if (!window.confirm('Abandon this sortie? Progress will be saved but the run will be marked abandoned.')) return;
    onAbandon(run.id);
    onBack();
  }

  function exportPdf() {
    const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
    const m = 40;
    const pw = 612;
    const mw = pw - m * 2;
    let y = m;

    // Header
    pdf.setFontSize(16);
    pdf.setTextColor(75, 0, 130);
    pdf.text(procedure.name, m, y);
    y += 18;
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    pdf.text(`Sortie: ${new Date(run.startDate).toLocaleDateString()} | ${doneCount} of ${totalCount} complete`, m, y);
    y += 16;
    pdf.setDrawColor(184, 134, 11);
    pdf.line(m, y, pw - m, y);
    y += 14;

    steps.forEach((step, i) => {
      if (y > 720) { pdf.addPage(); y = m; }
      const isDone = progress[`step_${i}`]?.done;
      const check = isDone ? '[x]' : '[ ]';
      pdf.setFontSize(10);
      pdf.setTextColor(isDone ? 150 : 30);
      pdf.setFont(undefined, 'bold');
      const titleLines = pdf.splitTextToSize(`${check} ${step.stepNumber}. ${step.title}`, mw);
      pdf.text(titleLines, m, y);
      pdf.setFont(undefined, 'normal');
      y += titleLines.length * 13 + 2;
      if (step.details) {
        if (y > 720) { pdf.addPage(); y = m; }
        pdf.setFontSize(9);
        pdf.setTextColor(isDone ? 170 : 80);
        const dl = pdf.splitTextToSize(step.details, mw - 15);
        pdf.text(dl, m + 15, y);
        y += dl.length * 12 + 2;
      }
      if (step.notes) {
        if (y > 720) { pdf.addPage(); y = m; }
        pdf.setFontSize(8);
        pdf.setTextColor(184, 134, 11);
        pdf.setFont(undefined, 'italic');
        const nl = pdf.splitTextToSize(step.notes, mw - 15);
        pdf.text(nl, m + 15, y);
        pdf.setFont(undefined, 'normal');
        y += nl.length * 11 + 4;
      }
      y += 4;
    });

    pdf.save(`Sortie_${procedure.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  // Detect if procedure was edited after sortie started (step count mismatch)
  const stepsChanged = Object.keys(progress).length !== steps.length;

  // --- FOCUS MODE ---
  if (focusMode) {
    if (allDone) {
      return (
        <div className="Sortie-focus">
          <div className="Sortie-focus-header">
            <span className="Sortie-focus-title">{procedure.name}</span>
            <button className="Sortie-focus-exit" onClick={toggleFocusMode}>Checklist</button>
          </div>
          <div className="Sortie-focus-body">
            <div className="Sortie-focus-done-text">SORTIE COMPLETE</div>
            <div className="Sortie-focus-done-stats">{doneCount} of {totalCount} steps</div>
            <button className="Sortie-focus-complete-btn" onClick={handleComplete}>Mark Complete</button>
            <button className="Sortie-focus-back-btn" onClick={onBack}>Back</button>
          </div>
        </div>
      );
    }

    const step = steps[currentIndex];
    const nextStep = currentIndex + 1 < steps.length ? steps[currentIndex + 1] : null;

    return (
      <div className="Sortie-focus">
        <div className="Sortie-focus-header">
          {role && <div className="Sortie-focus-role-bar" style={{ background: role.color }} />}
          <div className="Sortie-focus-header-row">
            <span className="Sortie-focus-title">{procedure.name}</span>
            <button className="Sortie-focus-exit" onClick={toggleFocusMode}>Checklist</button>
          </div>
          <div className="Sortie-focus-progress-bar">
            <div className="Sortie-focus-progress-fill" style={{ width: `${(doneCount / totalCount) * 100}%` }} />
          </div>
          <span className="Sortie-focus-progress-text">{doneCount} of {totalCount}</span>
        </div>

        <div className="Sortie-focus-body">
          <div className="Sortie-focus-step-num">Step {step.stepNumber}</div>
          <div className="Sortie-focus-step-title">{step.title}</div>
          {step.details && <div className="Sortie-focus-step-details">{step.details}</div>}
          {step.notes && <div className="Sortie-focus-step-notes">{step.notes}</div>}
        </div>

        {nextStep && (
          <div className="Sortie-focus-next">
            <span className="Sortie-focus-next-label">Next:</span>
            <span className="Sortie-focus-next-title">{nextStep.title}</span>
          </div>
        )}

        <div className="Sortie-focus-actions">
          <button className="Sortie-focus-done-btn" onClick={() => handleToggle(currentIndex)}>DONE</button>
          {onAskJBB && <button className="Sortie-focus-jbb-btn" onClick={() => onAskJBB(step, currentIndex)}>Ask JBB</button>}
        </div>
      </div>
    );
  }

  // --- CHECKLIST MODE ---
  return (
    <div className="Sortie">
      <div className="Sortie-header">
        {role && <div className="Sortie-role-bar" style={{ background: role.color }} />}
        <div className="Sortie-header-top">
          <button className="Sortie-back" onClick={onBack}>&larr;</button>
          <div className="Sortie-header-info">
            <h2 className="Sortie-name">{procedure.name}</h2>
            <span className="Sortie-meta">Started {new Date(run.startDate).toLocaleDateString()}</span>
          </div>
          <button className="Sortie-focus-toggle" onClick={toggleFocusMode}>Focus</button>
        </div>
        <div className="Sortie-progress-bar">
          <div className="Sortie-progress-fill" style={{ width: `${(doneCount / totalCount) * 100}%` }} />
        </div>
        <div className="Sortie-progress-text">{doneCount} of {totalCount} complete</div>
      </div>

      {stepsChanged && (
        <div className="Sortie-warning">This procedure has been modified since this sortie started. Step counts may differ.</div>
      )}

      <div className="Sortie-steps">
        {steps.map((step, i) => {
          const isDone = progress[`step_${i}`]?.done;
          const isCurrent = i === currentIndex;
          return (
            <div
              key={i}
              className={`Sortie-step${isDone ? ' Sortie-step--done' : ''}${isCurrent ? ' Sortie-step--current' : ''}`}
              onClick={() => handleToggle(i)}
            >
              <div className="Sortie-step-check">
                <span className={`Sortie-checkbox${isDone ? ' Sortie-checkbox--checked' : ''}`} />
              </div>
              <div className="Sortie-step-content">
                <div className="Sortie-step-header">
                  <span className="Sortie-step-num">{step.stepNumber}.</span>
                  <span className="Sortie-step-title">{step.title}</span>
                </div>
                {step.details && <p className="Sortie-step-details">{step.details}</p>}
                {step.notes && <div className="Sortie-step-notes">{step.notes}</div>}
                {isDone && progress[`step_${i}`]?.at && (
                  <span className="Sortie-step-time">{new Date(progress[`step_${i}`].at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="Sortie-bottom-bar">
        <div className="Sortie-bottom-left">
          <button className="Sortie-btn-secondary" onClick={onBack}>Save & Exit</button>
          <button className="Sortie-btn-secondary Sortie-btn-export" onClick={exportPdf}>PDF</button>
        </div>
        <div className="Sortie-bottom-right">
          {allDone && <button className="Sortie-btn-complete" onClick={handleComplete}>Complete</button>}
          {onAskJBB && currentIndex >= 0 && (
            <button className="Sortie-btn-primary" onClick={() => onAskJBB(steps[currentIndex], currentIndex)}>Ask JBB</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SortieRunner;
